import _ from "lodash";
import React, { useState, useMemo } from "react";

import update from "immutability-helper";
import classNames from "classnames";

import {
  DEFAULT_METADATA,
  DUAL_PANEL_VIEW_MODE,
  DUAL_PANEL_VIEW,
} from "../module/utils";

import ViewMembers from "./ViewMembers";
import UsersAndCohorts from "./UsersAndCohorts";
import MetadataProvider from "./MetadataProvider";

import classes from "./DualPanelManageMembers.scss";

const USERS_KEY = "users";

const initialState = {
  [DUAL_PANEL_VIEW[DUAL_PANEL_VIEW_MODE.GRADES].cohortKey]: {},
  [DUAL_PANEL_VIEW[DUAL_PANEL_VIEW_MODE.CLASSES].cohortKey]: {},
  //   sample structure with data
  //   [cohort_type]: { [id]: { usersIds: Set, removedUsersIds: Set}}
  // if contain remove use id then - intermediate state  this will sync when use user added or removed from

  [USERS_KEY]: {}, // this will contain all the users that are selected [id]: User as object
};

const DualPanelManageMembers = ({
  handleAddUsers,
  handleRemoveUsers,
  prevUsers = [],
  metadataProps,
  renderQueryWrapper,
  initUsersState = {},
  overrideClasses = {},
  testId,
  customQueryWrapperFields = {},
  filterComponent = null,
  leftPanelComponent = null,
}) => {
  const [cohortAndUserState, setCohortAndUserState] = useState({
    ...initialState,
    [USERS_KEY]: initUsersState,
  });
  const componentTestId = testId
    ? `${testId}-dualPaneManageMembers`
    : undefined;

  const handleClearAll = () => {
    handleRemoveUsers(_.values(cohortAndUserState[USERS_KEY]));
    setCohortAndUserState(initialState);
  };

  const addSelectedUsers = (users, cb) => {
    const userIds = new Set(users.map(user => user.id));
    setCohortAndUserState(prevState => {
      const updatedState = _.isFunction(cb) ? cb(prevState) : prevState;
      const stateWithAddedUsers = update(updatedState, {
        [USERS_KEY]: {
          $set: {
            ...updatedState[USERS_KEY],
            ...users.reduce((acc, user) => {
              acc[user.id] = user;
              return acc;
            }, {}),
          },
        },
      });

      const stateWithSyncCohortsState = update(stateWithAddedUsers, {
        $apply: state =>
          _.mapValues(state, (cohortTypeState, cohortType) => {
            if (!_.some(DUAL_PANEL_VIEW, mode => mode.cohortKey === cohortType))
              return cohortTypeState;

            return _.mapValues(cohortTypeState, cohortData =>
              update(cohortData, {
                removedUsersIds: {
                  $apply: removedUsersIds =>
                    new Set(
                      Array.from(removedUsersIds).filter(
                        removedUserId => !userIds.has(removedUserId)
                      )
                    ),
                },
              })
            );
          }),
      });

      return stateWithSyncCohortsState;
    });
    handleAddUsers(users);
  };

  const removeSelectedUsers = (users, cb) => {
    const userIdsList = _.map(users, "id");
    const userIds = new Set(userIdsList); // Using Set for faster lookup
    setCohortAndUserState(prevState => {
      const updatedState = _.isFunction(cb) ? cb(prevState) : prevState;
      const stateWithRemovedUser = update(updatedState, {
        [USERS_KEY]: {
          $unset: userIdsList,
        },
      });
      const stateWithSyncCohortsState = update(stateWithRemovedUser, {
        $apply: state => {
          const newState = _.mapValues(state, (cohortTypeState, cohortType) => {
            if (!_.some(DUAL_PANEL_VIEW, mode => mode.cohortKey === cohortType))
              return cohortTypeState;

            return _.mapValues(cohortTypeState, cohortData => {
              if (!cohortData) return undefined;

              const commonUserIds = new Set(
                [...cohortData.usersIds].filter(userId => userIds.has(userId))
              );

              // Add commonUserIds to removedUsersIds set
              const updatedRemovedUsersIds = new Set([
                ...cohortData.removedUsersIds,
                ...commonUserIds,
              ]);

              // Delete the cohort if removedUsersIds and usersIds are same
              if (
                _.isEqual(
                  _.size(updatedRemovedUsersIds),
                  _.size(cohortData.usersIds)
                )
              ) {
                return undefined; // Returning undefined deletes the key
              }

              return {
                ...cohortData,
                removedUsersIds: updatedRemovedUsersIds,
              };
            });
          });

          // Remove empty cohorts
          Object.keys(newState).forEach(cohortType => {
            if (!_.some(DUAL_PANEL_VIEW, mode => mode.cohortKey === cohortType))
              return newState;
            newState[cohortType] = _.pickBy(
              newState[cohortType],
              cohortData => cohortData !== undefined
            );
          });

          return newState;
        },
      });
      return stateWithSyncCohortsState;
    });

    handleRemoveUsers(users);
  };

  const onAddCohort = ({ users, cohortType, cohortId }) => {
    const stateAfterCohortAdded = prevState => {
      const usersIds = new Set(users.map(user => user.id));
      return update(prevState, {
        [cohortType]: {
          [cohortId]: {
            $set: {
              usersIds,
              removedUsersIds: new Set(),
            },
          },
        },
      });
    };
    addSelectedUsers(users, stateAfterCohortAdded);
  };

  const onRemoveCohort = ({ cohortType, cohortId }) => {
    const userIds = _.toArray(
      _.get(cohortAndUserState, [cohortType, cohortId, "usersIds"], [])
    );

    const users = _.map(userIds, userId =>
      _.get(cohortAndUserState, [USERS_KEY, userId], {})
    );

    const stateAfterCohortRemove = prevState => {
      return update(prevState, {
        [cohortType]: {
          $unset: [cohortId],
        },
      });
    };

    if (!_.isEmpty(users)) removeSelectedUsers(users, stateAfterCohortRemove);
  };

  const selectedUsers = useMemo(() => {
    return _.values(cohortAndUserState[USERS_KEY]);
  }, [cohortAndUserState]);

  const mergedMetadataProps = useMemo(() => {
    return _.merge({}, DEFAULT_METADATA, metadataProps);
  }, [metadataProps]);

  return (
    <MetadataProvider value={mergedMetadataProps}>
      <div className={classes.rootContainer}>
        {typeof leftPanelComponent === "function"
          ? leftPanelComponent({
              selectedUsers,
            })
          : leftPanelComponent}
        <div className={classes.cohortsAndUsersContainer}>
          <UsersAndCohorts
            cohortState={cohortAndUserState}
            selectedUsersState={cohortAndUserState[USERS_KEY]}
            onAddUsers={addSelectedUsers}
            onRemoveUsers={removeSelectedUsers}
            onAddCohort={onAddCohort}
            onRemoveCohort={onRemoveCohort}
            prevUsers={prevUsers}
            renderQueryWrapper={renderQueryWrapper}
            testId={componentTestId}
            customQueryWrapperFields={customQueryWrapperFields}
            filterComponent={filterComponent}
          />
        </div>
        <div
          className={classNames(
            classes.usersViewContainer,
            overrideClasses.usersViewContainer,
            {
              [classes.emptyState]: _.isEmpty(cohortAndUserState[USERS_KEY]),
            }
          )}
        >
          <ViewMembers
            users={selectedUsers}
            onRemoveUsers={removeSelectedUsers}
            handleClearAll={handleClearAll}
            testId={componentTestId}
          />
        </div>
      </div>
    </MetadataProvider>
  );
};

export default DualPanelManageMembers;
